import React, { useEffect, useState } from "react";
import { ChevronLeft, ExternalLink, Layers, X } from "lucide-react";
import { useFloatingWindows } from "@/lib/floatingWindows";
import { popOutWindow } from "@/lib/compactLayout";

const iconBtn = "h-10 min-w-10 px-2 grid place-items-center text-nex-chrome hover:text-nex-gold hover:bg-white/5 transition-colors";

function Switcher({ windows, top, titleFor, onClose }) {
  const { focusWindow, closeWindow, closeAll } = useFloatingWindows();
  const list = [...windows].sort((a, b) => b.z - a.z);
  return (
    <div data-testid="nds-compact-switcher" className="absolute inset-0 z-10 bg-black/60 flex items-end" onClick={onClose}>
      <div className="w-full max-h-[75%] overflow-y-auto bg-[#0B1220] border-t border-white/10 pb-[env(safe-area-inset-bottom)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <span className="font-mono text-[0.6rem] tracking-[0.24em] uppercase text-nex-gold">Open screens · {list.length}</span>
          <button type="button" data-testid="nds-compact-close-all" onClick={() => { closeAll(); onClose(); }}
            className="font-mono text-[0.6rem] tracking-[0.18em] uppercase text-nex-muted hover:text-nex-silver">Close all</button>
        </div>
        {list.map((w) => (
          <div key={w.id} data-testid={`nds-compact-switcher-item-${w.kind}`}
            className={`flex items-center gap-2 px-4 border-b border-white/5 ${w.id === top.id ? "bg-nex-gold/10" : ""}`}>
            <button type="button" onClick={() => { focusWindow(w.id); onClose(); }} className="flex-1 text-left py-3 text-sm text-nex-silver truncate">
              {titleFor(w)}{w.id === top.id && <span className="ml-2 text-[0.6rem] font-mono uppercase tracking-[0.18em] text-nex-gold">showing</span>}
            </button>
            <button type="button" aria-label="Close" onClick={() => closeWindow(w.id)} className={iconBtn}><X className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Phone / tablet window: one screen at a time, full screen (or a right-hand panel on a landscape tablet), never draggable. */
export default function CompactWindow({ win, windows, panel, kicker, title, subtitle, popoutHref, titleFor, children }) {
  const { closeWindow } = useFloatingWindows();
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (panel) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [panel]);

  useEffect(() => setSwitching(false), [win.id]);

  const pop = popoutHref
    ? <a href={popoutHref} target="_blank" rel="noopener noreferrer" data-testid={`nds-compact-popout-${win.kind}`} className={iconBtn} aria-label="Open in its own tab"><ExternalLink className="w-4 h-4" /></a>
    : <button type="button" onClick={() => { popOutWindow(win); closeWindow(win.id); }} data-testid={`nds-compact-popout-${win.kind}`} className={iconBtn} aria-label="Open in its own tab"><ExternalLink className="w-4 h-4" /></button>;

  return (
    <div role="dialog" aria-modal={!panel} data-testid={`nds-compact-window-${win.kind}`} data-window-id={win.id}
      className={`fixed z-[45] bg-[#0B1220] text-nex-silver flex flex-col pointer-events-auto animate-in fade-in slide-in-from-right-4 duration-200 ${
        panel ? "top-0 right-0 bottom-0 w-[min(760px,62vw)] border-l border-white/70 shadow-[-30px_0_80px_rgba(0,0,0,0.6)]" : "inset-0"}`}>
      <header className="flex items-center gap-1 pl-1 pr-2 border-b border-white/10 bg-white/[0.03] pt-[env(safe-area-inset-top)]">
        <button type="button" onClick={() => closeWindow(win.id)} data-testid="nds-compact-back" className={`${iconBtn} gap-1 font-mono text-[0.6rem] uppercase tracking-[0.18em]`}>
          <ChevronLeft className="w-5 h-5" /> Back
        </button>
        <div className="flex-1 min-w-0 py-2">
          {kicker && <div className="font-mono text-[0.55rem] tracking-[0.22em] text-nex-gold uppercase truncate">{kicker}</div>}
          <div data-testid="nds-compact-title" className="font-semibold text-sm truncate">{title}</div>
          {subtitle && <div className="text-[0.65rem] text-nex-muted truncate">{subtitle}</div>}
        </div>
        {windows.length > 1 && (
          <button type="button" onClick={() => setSwitching(true)} data-testid="nds-compact-switcher-btn" className={`${iconBtn} gap-1 text-xs`} aria-label="Open screens">
            <Layers className="w-4 h-4" /> {windows.length}
          </button>
        )}
        {pop}
      </header>
      <div data-testid="nds-compact-body" className="nds-compact-body flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y pb-[env(safe-area-inset-bottom)]">{children}</div>
      {switching && <Switcher windows={windows} top={win} titleFor={titleFor} onClose={() => setSwitching(false)} />}
    </div>
  );
}
